import React from 'react';
import { InfographicData } from '../types';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';

interface Props {
  data?: InfographicData;
}

const COLORS = ['#4f46e5', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

const Infographic: React.FC<Props> = ({ data }) => {
  if (!data) {
    return (
      <div className="flex items-center justify-center h-[600px] bg-slate-50 rounded-xl border border-slate-200">
        <p className="text-slate-500 font-medium">No infographic data available.</p>
      </div>
    );
  }

  return (
    <div className="w-full bg-slate-50 rounded-2xl border border-slate-200 p-8 space-y-8">
      <div className="text-center">
        <h2 className="text-3xl font-black text-slate-900 tracking-tight">{data.title}</h2>
        <div className="h-1 w-16 bg-indigo-500 mx-auto mt-4 rounded-full"></div>
      </div>
      
      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {data.stats.map((stat, idx) => (
          <div key={idx} className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 flex flex-col items-center justify-center text-center transition-transform hover:-translate-y-1 duration-300">
            <span className="text-4xl font-black text-indigo-600 mb-2">{stat.value}</span>
            <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">{stat.label}</span>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Chart Section */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 flex flex-col">
          <h3 className="text-xl font-bold text-slate-900 mb-6 flex items-center">
            <svg className="w-5 h-5 text-indigo-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
            </svg>
            {data.chart.title}
          </h3>
          <div className="flex-grow min-h-[250px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data.chart.data}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={80}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {data.chart.data.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }}
                  itemStyle={{ fontWeight: 'bold' }}
                />
                <Legend verticalAlign="bottom" height={36} iconType="circle" wrapperStyle={{ fontSize: '12px', fontWeight: '600', color: '#64748b' }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Timeline Section */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
          <h3 className="text-xl font-bold text-slate-900 mb-6 flex items-center">
            <svg className="w-5 h-5 text-emerald-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Key Milestones
          </h3>
          <div className="relative border-l-2 border-slate-100 ml-3 space-y-8 mt-4">
            {data.timeline.map((item, idx) => (
              <div key={idx} className="relative pl-6">
                <div className="absolute w-4 h-4 bg-emerald-500 rounded-full -left-[9px] top-1 border-4 border-white shadow-sm"></div>
                <div className="text-xs font-bold text-emerald-600 mb-1 uppercase tracking-wider">{item.date}</div>
                <div className="text-sm font-semibold text-slate-800">{item.event}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Infographic;
